import { useState } from 'react';
import { toast } from 'sonner';
import { generatePreviewPayslipPdf, generatePreviewCSV, downloadBlob, downloadString } from '@/services/payslips/previewPdfGenerator';
import { getExportPreview, DEFAULT_EXPORT_OPTIONS } from '@/services/payslips/exportConfig';
import type { ExportContentOptions } from '@/services/payslips/exportConfig';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Download, 
  FileSpreadsheet, 
  FileText,
  CheckCircle,
  Settings
} from 'lucide-react';

import { format } from 'date-fns';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { PayrollPreviewResult } from '@/services/compensation/types';

interface PayrollExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payrollData: PayrollPreviewResult[];
  payPeriodStart: Date;
  payPeriodEnd: Date;
}

type ExportFormat = 'csv' | 'pdf' | 'xlsx';

export function PayrollExportDialog({
  open,
  onOpenChange,
  payrollData,
  payPeriodStart,
  payPeriodEnd,
}: PayrollExportDialogProps) {
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [contentOptions, setContentOptions] = useState<ExportContentOptions>({ ...DEFAULT_EXPORT_OPTIONS });
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  // Derive total hours from the calculation summary (not timeEntries)
  const getTotalSummary = () => {
    return payrollData.reduce(
      (acc, result) => {
        const s = result.calculation.calculationSummary;
        return {
          totalEmployees: acc.totalEmployees + 1,
          totalGross: acc.totalGross + result.calculation.totalGrossPay,
          totalNet: acc.totalNet + result.calculation.netPay,
          totalHours: acc.totalHours + s.regularHours + s.overtimeHours + s.holidayHours + s.paidLeaveHours,
        };
      },
      { totalEmployees: 0, totalGross: 0, totalNet: 0, totalHours: 0 }
    );
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      if (exportFormat === 'xlsx') {
        toast.info('Excel export is coming soon');
        setIsExporting(false);
        return;
      }

      if (exportFormat === 'csv') {
        const { content, filename } = generatePreviewCSV(
          payrollData, payPeriodStart, payPeriodEnd, currencyCode, contentOptions,
        );
        downloadString(content, filename, 'text/csv;charset=utf-8;');
      } else if (exportFormat === 'pdf') {
        for (const employee of payrollData) {
          const blob = await generatePreviewPayslipPdf(
            employee, payPeriodStart, payPeriodEnd,
            { currencyCode, contentOptions },
          );
          const filename = `payslip_preview_${employee.employeeName.replace(/\s+/g, '_')}_${format(payPeriodStart, 'yyyy-MM-dd')}.pdf`;
          downloadBlob(blob, filename);
        }
      }

      setExportComplete(true);
      toast.success(`${exportFormat.toUpperCase()} export completed`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const updateContentOption = (key: keyof ExportContentOptions, value: boolean) => {
    setContentOptions(prev => ({ ...prev, [key]: value }));
  };

  const summary = getTotalSummary();
  const exportPreview = getExportPreview(exportFormat, contentOptions, summary.totalEmployees);

  if (exportComplete) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Export Complete
            </DialogTitle>
            <DialogDescription>
              Your payroll export has been generated and downloaded successfully.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="font-medium">Export Successful</p>
                <p className="text-sm text-muted-foreground">
                  File downloaded: payroll_{format(payPeriodStart, 'yyyy-MM-dd')}.{exportFormat}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setExportComplete(false);
                onOpenChange(false);
              }}
              className="flex-1"
            >
              Close
            </Button>
            <Button onClick={handleExport} className="flex-1">
              Export Again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Payroll Data
          </DialogTitle>
          <DialogDescription>
            Export payroll calculations for {summary.totalEmployees} employees
            ({format(payPeriodStart, 'MMM dd')} - {format(payPeriodEnd, 'MMM dd, yyyy')})
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Export Options */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Export Options</h3>

              {/* Format Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Export Format</Label>
                <Select
                  value={exportFormat}
                  onValueChange={(v) => setExportFormat(v as ExportFormat)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        CSV - Spreadsheet Format
                      </div>
                    </SelectItem>
                    <SelectItem value="xlsx" disabled>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 opacity-50" />
                        <span className="opacity-50">Excel - Multi-sheet Workbook</span>
                        <Badge variant="secondary" className="text-xs ml-1">Coming soon</Badge>
                      </div>
                    </SelectItem>
                    <SelectItem value="pdf">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        PDF - Draft Payslips
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator className="my-4" />

              {/* Content Options — every option maps to real data */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Content Options</Label>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeLineItems"
                      checked={contentOptions.includeLineItems}
                      onCheckedChange={(checked) => updateContentOption('includeLineItems', !!checked)}
                    />
                    <Label htmlFor="includeLineItems" className="text-sm">
                      {exportFormat === 'csv'
                        ? 'Include line-item breakdown (adds rows per earning/deduction)'
                        : 'Include earnings & deductions table'}
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeHoursBreakdown"
                      checked={contentOptions.includeHoursBreakdown}
                      onCheckedChange={(checked) => updateContentOption('includeHoursBreakdown', !!checked)}
                    />
                    <Label htmlFor="includeHoursBreakdown" className="text-sm">
                      Include hours breakdown (regular, overtime, holiday, paid leave)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeYTD"
                      checked={contentOptions.includeYTD}
                      onCheckedChange={(checked) => updateContentOption('includeYTD', !!checked)}
                    />
                    <Label htmlFor="includeYTD" className="text-sm">
                      Include year-to-date totals
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview & Summary */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Export Preview</h3>

              {/* Summary Stats */}
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Payroll Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Employees</p>
                      <p className="font-semibold">{summary.totalEmployees}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Hours</p>
                      <p className="font-semibold">{summary.totalHours.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Gross Pay</p>
                      <p className="font-semibold text-green-600">
                        {formatCurrency(summary.totalGross)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Net Pay</p>
                      <p className="font-semibold">
                        {formatCurrency(summary.totalNet)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Export Preview — generated from the same config as the actual export */}
              {exportPreview && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{exportPreview.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">{exportPreview.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <p className="text-sm font-medium mb-2">
                        {exportFormat === 'csv' ? 'Columns included:' : 'Sections included:'}
                      </p>
                      <div className="space-y-1">
                        {exportPreview.columns.map((column, index) => (
                          <div key={index} className="flex items-center gap-2 text-xs">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            <span>{column}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Actions */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button
            onClick={handleExport}
            disabled={isExporting || payrollData.length === 0}
            className="min-w-32"
          >
            {isExporting ? (
              <>
                <Settings className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export {exportFormat.toUpperCase()}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
